<script>
import Title from "@/components/admin/common/TitleArea.vue"

const allEventTypes = [
  { label: '로그인 성공', value: 'auth.login' },
  { label: '로그인 실패', value: 'auth.login_fail' },
  { label: '로그아웃', value: 'auth.logout' },
  { label: '재설정 링크 생성 성공', value: 'auth.password_reset_link_create' },
  { label: '재설정 링크 생성 실패', value: 'auth.password_reset_link_create_fail' }
]

export default {
  name: "Admin-login-history-manage",
  meta: { title: "어드민-인증·접속 이력" },
  components: { Title },
  data() {
    return {
      description: {
        title: '인증·접속 이력',
        subTitle: '로그인 성공·실패와 계정 보안 이벤트를 조회합니다. IP는 개인정보 보호를 위해 일부 마스킹됩니다.'
      },
      loginList: [],
      loading: false,
      errorMessage: '',
      startDate: '',
      endDate: '',
      userId: '',
      partnerId: '',
      successFilter: null,
      selectedEventTypes: allEventTypes.map((item) => item.value),
      eventTypeOptions: allEventTypes,
      successOptions: [
        { label: '전체', value: null },
        { label: '성공', value: 1 },
        { label: '실패', value: 0 }
      ],
      pagination: { page: 1, rowsPerPage: 50, rowsNumber: 0 },
      columns: [
        { name: 'ingested_at', label: '기록 시간 (Australia/Sydney)', align: 'center', field: 'ingested_at', format: this.formatDateTime },
        { name: 'reg_name', label: '사용자명', align: 'center', field: 'reg_name' },
        { name: 'partnerName', label: '파트너명', align: 'center', field: 'partnerName' },
        { name: 'event_type', label: '인증 이벤트', align: 'center', field: 'event_type' },
        { name: 'method', label: '메서드', align: 'center', field: 'method' },
        { name: 'url_path', label: 'URL 경로', align: 'left', field: 'url_path' },
        { name: 'status', label: '상태', align: 'center', field: 'status' },
        { name: 'ok', label: '결과', align: 'center', field: 'ok', format: (value) => Number(value) === 1 ? '성공' : '실패' },
        { name: 'ip', label: 'IP(마스킹)', align: 'center', field: 'ip' }
      ]
    }
  },
  methods: {
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        timeZoneName: 'short'
      }).format(date)
    },
    endExclusive(value) {
      if (!value) return undefined
      return new Date(`${value}T00:00:00.000Z`).getTime() + 86400000
    },
    buildParams() {
      const params = {
        page: this.pagination.page,
        page_size: this.pagination.rowsPerPage
      }
      if (this.startDate) params.start = `${this.startDate}T00:00:00.000Z`
      if (this.endDate) params.end = new Date(this.endExclusive(this.endDate)).toISOString()
      if (this.userId.trim()) params.user_id = this.userId.trim()
      if (this.partnerId.trim()) params.partner_id = this.partnerId.trim()
      if (this.successFilter !== null) params.ok = this.successFilter
      if (this.selectedEventTypes.length) params.event_types = this.selectedEventTypes.join(',')
      return params
    },
    async fetchLoginHistory() {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await this.$http({
          method: 'get', url: '/api/log/login-list',
          name: '[ Axios / 인증·접속 이력 조회 ]', params: this.buildParams()
        })
        this.loginList = res.data?.items || []
        this.pagination.rowsNumber = Number(res.data?.pagination?.total || 0)
      } catch (error) {
        this.loginList = []
        this.pagination.rowsNumber = 0
        this.errorMessage = error?.response?.data?.message || '인증·접속 이력을 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    async applyFilters() {
      this.pagination.page = 1
      await this.fetchLoginHistory()
    },
    async resetFilters() {
      this.startDate = ''
      this.endDate = ''
      this.userId = ''
      this.partnerId = ''
      this.successFilter = null
      this.selectedEventTypes = this.eventTypeOptions.map((item) => item.value)
      await this.applyFilters()
    },
    async onRequest({ pagination }) {
      this.pagination.page = pagination.page
      this.pagination.rowsPerPage = pagination.rowsPerPage
      await this.fetchLoginHistory()
    }
  },
  mounted() {
    this.fetchLoginHistory()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg"><div class="col"><Title :desc="description" /></div></div>
    <div class="comp-box">
      <div class="text-body2 text-grey-7 q-mb-md">
        계정 보안 감사용 화면입니다. 로그인 실패를 포함하며, 최대 100건씩 서버에서 조회합니다.
      </div>
      <div class="row q-col-gutter-md q-mb-md">
        <div class="col-12 col-sm-6 col-md-2"><q-input v-model="startDate" type="date" label="시작일" clearable dense outlined /></div>
        <div class="col-12 col-sm-6 col-md-2"><q-input v-model="endDate" type="date" label="종료일" clearable dense outlined /></div>
        <div class="col-12 col-sm-6 col-md-2"><q-input v-model="userId" label="사용자 ID" clearable dense outlined /></div>
        <div class="col-12 col-sm-6 col-md-2"><q-input v-model="partnerId" label="파트너 ID" clearable dense outlined /></div>
        <div class="col-12 col-sm-6 col-md-2">
          <q-select v-model="successFilter" label="성공 여부" :options="successOptions" emit-value map-options dense outlined />
        </div>
      </div>
      <div class="row q-col-gutter-md q-mb-md items-center">
        <div class="col-12 col-md-9">
          <q-select v-model="selectedEventTypes" label="인증 이벤트" :options="eventTypeOptions" emit-value map-options multiple use-chips dense outlined />
        </div>
        <div class="col-12 col-md-3 row q-gutter-sm">
          <q-btn color="primary" label="조회" :loading="loading" @click="applyFilters" />
          <q-btn flat color="grey-8" label="초기화" :disable="loading" @click="resetFilters" />
        </div>
      </div>
      <q-banner v-if="errorMessage" class="bg-red-1 text-negative q-mb-md" rounded>{{ errorMessage }}</q-banner>
      <q-table
        :rows="loginList"
        :columns="columns"
        row-key="id"
        :loading="loading"
        v-model:pagination="pagination"
        :rows-per-page-options="[25, 50, 100]"
        no-data-label="조건에 맞는 인증 이력이 없습니다."
        @request="onRequest" />
    </div>
  </q-page>
</template>

<style scoped></style>
